import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  fetchCreditBureauActive,
  fetchCreditBureauDetails,
  type CreditBureauConfig,
} from "@/lib/api/applicationService";
import { getCreditBureauDisplay } from "../utils/creditBureauDisplay";
import styles from "../styles/applicationDetail.module.css";
import type { ApplicationAgreementsSummaryProps } from '../types/components'



/**
 * Admin read-only agreements block — Angular `app-summary-info` with `[fromAdmin]="true"`.
 */
export function ApplicationAgreementsSummary({
  applicantId,
}: ApplicationAgreementsSummaryProps) {
  const [creditBureauConfig, setCreditBureauConfig] =
    useState<CreditBureauConfig | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const cbActive = await fetchCreditBureauActive();
        let config = cbActive;
        if (applicantId) {
          const cbDetails = await fetchCreditBureauDetails(applicantId);
          if (cbDetails?.active) config = cbDetails;
        }
        if (!cancelled) setCreditBureauConfig(config);
      } catch {
        /* fall back to Equifax defaults in getCreditBureauDisplay */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applicantId]);

  const bureau = getCreditBureauDisplay(creditBureauConfig);
  const creditReportingPath = applicantId
    ? `/resident-application/terms_info/credit_reporting_bureau/${applicantId}`
    : "/resident-application/terms_info/credit_reporting_bureau";

  return (
    <div className={styles.agreementsSummaryMain} data-testid="application-detail-agreements-summary">
      <p data-testid="application-detail-agreements-spanish-notice">
        Para informatión en español, visite{" "}
        <a
          href="https://www.consumerfinance.gov/learnmore"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="application-detail-agreements-cfpb-link"
        >
          www.consumerfinance.gov/learnmore
        </a>{" "}
        o escribe a la Consumer Financial Protection Bureau, 1700 G Street N.W.,
        Washington, DC 20552.
      </p>

      <div className={styles.agreementsSummaryLinkBlock} data-testid="application-detail-agreements-fair-housing-block">
        <h2 className={styles.agreementsSummaryTitle}>
          <Link
            to="/resident-application/terms_info/fair_housing_act"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="application-detail-agreements-fair-housing-link"
          >
            A Summary of Your Rights Under the Fair Credit Reporting Act
          </Link>
        </h2>
      </div>

      <div className={styles.agreementsSummaryLinkBlock} data-testid="application-detail-agreements-california-law-block">
        <h2 className={styles.agreementsSummaryTitle}>
          <Link
            to={creditReportingPath}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="application-detail-agreements-california-law-link"
          >
            Notice Regarding Background Investigation Pursuant to California
            Law
          </Link>
        </h2>
      </div>

      <div className={styles.agreementsSummaryLinkBlock} data-testid="application-detail-agreements-authorization-block">
        <h2 className={styles.agreementsSummaryTitle}>
          <Link
            to="/resident-application/terms_info/authorization_for_liberty"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="application-detail-agreements-authorization-link"
          >
            Authorization for Liberty Rent to Collect Payments
          </Link>
        </h2>
      </div>

      <h2 className={styles.agreementsSummaryHeading} data-testid="application-detail-agreements-acknowledgment-heading">
        Acknowledgment and Authorization
      </h2>
      <p>
        <strong>[ IMPORTANT -- PLEASE READ CAREFULLY BEFORE SIGNING ACKNOWLEDGMENT ]</strong>
      </p>

      <p>
        I acknowledge receipt of the DISCLOSURE REGARDING BACKGROUND INVESTIGATION
        and A SUMMARY OF YOUR RIGHTS UNDER THE FAIR CREDIT REPORTING ACT and
        certify that I have read and understand both of those documents. I hereby
        authorize the obtaining of consumer reports and/or investigative consumer
        reports at any time after receipt of this authorization and, if I am
        approved for residency, throughout my tenancy. To this end, I hereby
        authorize, without reservation, any law enforcement agency, administrator,
        state or federal agency, institution, school or university (public or
        private), information service bureau, employer, or insurance company to
        furnish any and all background information requested by{" "}
        <strong>
          {bureau.name}, {bureau.address}, Phone: {bureau.phone}
          {bureau.fax ? `, Fax: ${bureau.fax}` : ""}
          {bureau.web ? (
            <>
              , Web:{" "}
              <a
                href={bureau.web}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="application-detail-agreements-bureau-web-link"
              >
                {bureau.web}
              </a>
            </>
          ) : null}{" "}
          {bureau.equifaxHint ? `${bureau.equifaxHint}, ` : ""}
        </strong>
        another outside organization acting on behalf of the Company, and/or the
        Company itself. I agree that an electronic or photographic copy of this
        Authorization shall be as valid as the original.
      </p>

      <p>
        I understand that by typing my name and the last four digits of my Social
        Security Number or User ID, and clicking on the &quot;Next&quot; button below,
        constitutes my electronic signature, dated as of when I click on the
        &quot;Sign Application&quot; button, and that by doing so:
      </p>

      <ul className={styles.agreementsSummaryList} data-testid="application-detail-agreements-acknowledgment-list">
        <li>
          <p>
            I am authorizing <strong>{bureau.name}</strong> to conduct the
            background check(s) described above
          </p>
        </li>
        <li>
          <p>
            I am consenting to use electronic means to sign this form and have
            read and understand the above disclosure
          </p>
        </li>
        <li>
          <p>
            I certify I have read and understand the{" "}
            <strong>
              Summary Of Your Rights Under The Fair Credit Reporting Act
            </strong>
          </p>
        </li>
        <li>
          <p>
            I certify I have read and understand the{" "}
            <strong>
              Notice Regarding Background Investigation Pursuant To California
              Law
            </strong>
          </p>
        </li>
        <li>
          <p>
            I certify that the information submitted is correct and true.{" "}
            <strong>
              I understand that providing false or tampered with documentation
              will result in a denial of my application.
            </strong>
          </p>
        </li>
        <li>
          <p>
            I certify I have read, agree to the Authorization for{" "}
            <strong>Liberty Rent to Collect Payments.</strong>
          </p>
        </li>
        <li>
          <p>
            The applicant expressly gives permission to applicant property to
            share personal information with <strong>Liberty Rent</strong> through
            the life of the lease contract.
          </p>
        </li>
        <li>
          <p>
            <strong>
              I understand that my Liberty Rent Application Fee is
              Non-Refundable, unless I am Denied by Liberty Rent.
            </strong>
          </p>
        </li>
        <li>
          <p>
            <strong>
              I understand that the Liberty Rent Certificate Fee is
              Non-Refundable.
            </strong>
          </p>
        </li>
        <li>
          <p>
            I acknowledge I may request a hard copy of this Disclosure and
            Authorization form after agreeing to the background check
            electronically by emailing <strong>Liberty Rent</strong> at{" "}
            <strong>
              Email:{" "}
              <a href="mailto:help@libertyrent.com" data-testid="application-detail-agreements-help-email-link">
                help@libertyrent.com
              </a>
            </strong>
          </p>
        </li>
      </ul>

      <p>
        I hereby consent to receive disclosures and information electronically on
        this website and to electronically sign documents.
      </p>
      <p>
        If you do not wish to complete and sign this application electronically,
        please contact us for instructions.
      </p>
    </div>
  );
}
